import './listing-search.css';

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { LISTING_SEARCH_API_URL } from '../util/listing-search.constants';
import { ICoord } from '../models/coord.model';
import { defaultValue as defaultIListings, IListings } from '../models/listings.model';
import { IListing } from '../models/listing.model';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { listingUrl } from '../util/listing-urls';
import { SearchForm } from './search-form';
import SavedListings from './saved-listings';
import { Results } from './results/results';
import { REQUEST, SUCCESS, FAILURE } from '../util/action-type.util';
import { listingsSearchParamsToApi } from '../util/listings-search-params-to-api';
import { debounce, isEmpty, isEqual } from 'lodash';
import { SEARCH_RESULTS_PAGE_PATH_STR } from '../util/google.constants';
import { MapContainer } from './map/index';
import { ISearchParamsCache, defaultValue as defaultSearchParamsCache } from '../models/search-params-cache.model';

export const ACTION_TYPES = {
  FETCH_LISTINGS: 'listings/FETCH',
  RESET_LISTINGS: 'listings/RESET',
};

export interface IListingSearch {
  mapCenterCoord: ICoord;
  mapFitBounds: google.maps.LatLngBounds | null,
  locationAutocompleteValue: google.maps.places.AutocompletePrediction | null;
  isMapCenterInitialized: boolean;
  savedListings: IListings;
  searchType: string;
  forcedSearchParams: ISearchParamsCache;
  isHeaderDisabled: boolean;
  isSavedListing(id: string): boolean;
  addSavedListing(value: IListing | null): void;
  removeSavedListing(value: IListing | null): void;
  setMapCenterCoord(value: ICoord): void;
  centerMapToLocation(): void;
  setMapCenterToPlaceId(value: string): void;
  setLocationAutocompleteValue(value: google.maps.places.AutocompletePrediction): void;
  centerMapToPlace(): void;
  setPrevSearchUrl(value: string): void;
}

function testIsPaginatedUrl(url: string) {
  const pattern = /\/p_\d+\//;
  if (pattern.test(url)) {
    return true;
  } else {
    return false;
  }
}

function findPaginatorSlug(url: string) {
  const pattern = /\/([^/]+)/g;
  let match;
  while ((match = pattern.exec(url)) !== null) {
    const slug = match[1];
    if (slug.includes("p_")) {
      return slug;
    }
  }
  return '';
}

const getCurrentPageFromUrl = (url: string) => {
  let page = 1;
  if (testIsPaginatedUrl(url)) {
    const slug = findPaginatorSlug(url);
    page = Number(slug.replace(SEARCH_RESULTS_PAGE_PATH_STR, ''))
  }
  return page;
};


export const ListingSearch: React.FC<IListingSearch> = (props: IListingSearch) => {
  const isMapSearch = useMemo(() => { return props.searchType === 'map' }, [props.searchType]);
  const isFlatSearch = useMemo(() => { return props.searchType === 'flat' }, [props.searchType]);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  let currentPathNoPage = currentPath;

  const startIndex = currentPath.indexOf("/p_");

  if (startIndex !== -1) {
    currentPathNoPage = currentPath.substring(0, (startIndex + 1));
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchParamsCache, setSearchParamsCache] = useState<ISearchParamsCache>(defaultSearchParamsCache);

  const controller = new AbortController();
  const signal = controller.signal;
  const [activePreviewListingId, setActivePreviewListingId] = useState('');

  const [savedSearches, setSavedSearches] = useState<string[]>([]);
  const prevGeoPolySearch = useRef<ICoord[]>([]);
  const [geoPolySearch, setGeoPolySearch] = useState<ICoord[]>([]);
  const [sortBy, setSortBy] = useState('newest');

  const curPageMemo = useMemo(() => { return getCurrentPageFromUrl(location.pathname) }, [location.pathname]);
  const [currentPage, setCurrentPage] = useState(curPageMemo);

  const updatePrevSearchUrl = () => {
    // update previous search url ONLY when it's not a details page, we assume every other page is a search page
    !location.pathname.startsWith('/details') &&
      props.setPrevSearchUrl(location.pathname + '?' + searchParams.toString());
  };

  const [listings, dispatch] = useReducer((state: any, action: { type: any; payload: { data: any; }; }) => {
    switch (action.type) {
      case REQUEST(ACTION_TYPES.FETCH_LISTINGS):
        return {
          ...state,
          loading: true,
          errorMessage: null
        };
      case SUCCESS(ACTION_TYPES.FETCH_LISTINGS):
        return {
          ...state,
          ...action.payload.data,
          loading: false,
          errorMessage: null
        };
      case FAILURE(ACTION_TYPES.FETCH_LISTINGS):
        return {
          ...state,
          loading: false,
          errorMessage: action.payload.data
        };
      case ACTION_TYPES.RESET_LISTINGS:
        return {
          ...state,
          ...defaultIListings,
          loading: false,
          errorMessage: null
        };
      default:
        return state;
    }
  }, []);

  const setActivePreviewListingIdWrapper = useCallback(function (id: string) {
    setActivePreviewListingId(id)
  }, []);

  const setSearchOption = useCallback(function (key: string, value: string | number | object) {
    if (typeof (value) === 'object') {
      if (isEmpty(value)) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, JSON.stringify(value));
      }
    } else if (typeof (value) === 'number') {
      if (value === 0) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value.toString());
      }
    } else {
      if (isEmpty(value) || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    }
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const setMapCenterCoord = useCallback(function (value: ICoord) {

    // remove any bounds
    setSearchOption('b', '');
    setSearchOption('l', '');

    // on set search option if url is not a listing details display
    !location.pathname.startsWith('/details') &&
      setSearchOption('x', [value.lat, value.lng]);

    props.setMapCenterCoord(value);
  }, [location.pathname, props, setSearchOption]);

  const fetchListingsRequest = useCallback(function () {
    dispatch({
      type: REQUEST(ACTION_TYPES.FETCH_LISTINGS),
      payload: { data: {} }
    });
  }, []);

  const fetchListingsFail = useCallback(function (data: any) {
    dispatch({
      type: FAILURE(ACTION_TYPES.FETCH_LISTINGS),
      payload: { data }
    });
  }, []);

  const abortableFetchListings = useCallback(function () {
    const queryString = listingsSearchParamsToApi(searchParamsCache, props.forcedSearchParams, geoPolySearch, currentPage);

    fetch(LISTING_SEARCH_API_URL + '?' + queryString, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      signal
    }).then(response => response.json())
      .then(data => dispatch({
        type: SUCCESS(ACTION_TYPES.FETCH_LISTINGS),
        payload: { data }
      }),
        (err: TypeError) => {
          err.name !== 'AbortError' &&
            fetchListingsFail(err);
        });
  }, [currentPage, fetchListingsFail, geoPolySearch, searchParamsCache, signal, props.forcedSearchParams]);

  const debouncedSearch = debounce(abortableFetchListings, 1000);

  const openListing = useCallback(function (listing: IListing) {
    navigate(listingUrl(listing));
  }, [navigate]);

  const resetPageUrl = useCallback(function () {
    // force 
    if (!location.pathname.startsWith('/details')) {
      const searchParamsStr = searchParams.toString();
      navigate(currentPathNoPage + (searchParamsStr.length > 0 ? ('?' + searchParamsStr) : ''));
    }
  }, [location.pathname, navigate, searchParams, currentPathNoPage]);

  const searchForm = useMemo(() => <SearchForm
    isLocationDisabled={isFlatSearch}
    searchParamsCache={searchParamsCache}
    locationAutocompleteValue={props.locationAutocompleteValue}
    savedSearches={savedSearches}
    setSearchOption={setSearchOption}
    setLocationAutocompleteValue={props.setLocationAutocompleteValue}
    setSavedSearches={setSavedSearches}
    centerMapToLocation={props.centerMapToLocation}
    centerMapToPlace={props.centerMapToPlace}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  />, [searchParamsCache, savedSearches, props.centerMapToPlace]);

  const results = useMemo(() => <Results
    title="Real Estate Listings"
    isHeaderDisabled={props.isHeaderDisabled}
    listings={listings}
    sortBy={sortBy}
    activePreviewListingId={activePreviewListingId}
    isSavedListing={props.isSavedListing}
    setSortBy={setSortBy}
    addSavedListing={props.addSavedListing}
    removeSavedListing={props.removeSavedListing}
    setActivePreviewListingId={setActivePreviewListingIdWrapper}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  />, [listings, activePreviewListingId, props.savedListings]);

  const savedListingsResults = useMemo(() => <SavedListings
    savedListings={props.savedListings}
    activePreviewListingId={activePreviewListingId}
    isSavedListing={props.isSavedListing}
    addSavedListing={props.addSavedListing}
    removeSavedListing={props.removeSavedListing}
    setActivePreviewListingId={setActivePreviewListingIdWrapper}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  />, [props.savedListings, activePreviewListingId]);

  const mapContainer = useMemo(() => !isMapSearch ? <div /> : <MapContainer
    listings={listings}
    mapCenterCoord={props.mapCenterCoord}
    mapFitBounds={props.mapFitBounds}
    activePreviewListingId={activePreviewListingId}
    isMapCenterInitialized={props.isMapCenterInitialized}
    setActivePreviewListingId={setActivePreviewListingIdWrapper}
    setGeoPolySearch={setGeoPolySearch}
    setMapCenterCoord={setMapCenterCoord}
    openListing={openListing}
    updatePrevSearchUrl={updatePrevSearchUrl}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  />, [listings, props.mapCenterCoord, activePreviewListingId, props.isMapCenterInitialized, props.searchType]);

  useEffect(() => {
    if ((isFlatSearch && !location.pathname.startsWith('/details')) || (props.mapCenterCoord.lat !== 0 && geoPolySearch.length > 0 && location.pathname.startsWith('/search'))) {
      currentPage !== getCurrentPageFromUrl(location.pathname) &&
        setCurrentPage(getCurrentPageFromUrl(location.pathname));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    let tmp = {};
    if (location.pathname.startsWith('/search') || isFlatSearch) {
      searchParams.forEach((value, key) =>
        key !== 'x' && (tmp = { ...tmp, ...{ [key]: value } })
      );
      // check to see if they are the same before updating and forcing a render
      if (!isEqual(searchParamsCache, tmp)) {
        setSearchParamsCache(tmp);
        resetPageUrl();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const runSearch = useCallback(function () {
    try {
      if (isFlatSearch) {

        if (!location.pathname.startsWith('/details')) {
          fetchListingsRequest();
          debouncedSearch();
          updatePrevSearchUrl();
        }
      } else if (geoPolySearch.length > 0) {
        fetchListingsRequest();
        debouncedSearch();
        if (props.mapCenterCoord.lat !== 0 && geoPolySearch.length > 0 && location.pathname.startsWith('/search')) {
          updatePrevSearchUrl();
        }
      }
    } catch (e) {

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, fetchListingsRequest, geoPolySearch.length, location.pathname, props, searchParams, isFlatSearch]);

  useEffect(() => {
    if (isFlatSearch) {
      runSearch();
    } else if (geoPolySearch.length > 0) {
      if (!isEqual(prevGeoPolySearch.current, geoPolySearch)) {
        prevGeoPolySearch.current = geoPolySearch;
        resetPageUrl();
      }
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsCache, geoPolySearch, currentPage]);

  const cssClasses = useMemo(() => isMapSearch ? 'listing-search listing-search-is-map' : 'listing-search', [isMapSearch]);

  return (
    <div className={cssClasses}>
      {!props.isHeaderDisabled &&
        <div className="listing-search-top">
          <div className="listing-search-top-left">
            {searchForm}
          </div>
          <div className="listing-search-top-right">
            {savedListingsResults}
          </div>
        </div>
      }
      {isMapSearch &&
        <div className="listing-search-bottom">
          <div className="listing-search-map-container">
            {mapContainer}
          </div>
          <div className="listing-search-results-container">
            {results}
          </div>
        </div>
      }
      {isFlatSearch &&
        <div className="listing-search-bottom-flat">
          {results}
        </div>
      }
    </div>
  );
}

export default ListingSearch;
